import React, { useState, useEffect, useRef } from 'react';

// --- Configuration ---
const PLUTO_IP = "192.168.2.1";
const WEBSOCKET_URL = `wss://${PLUTO_IP}/waterfall`;

// -- Noise Profiling Config --
const PROFILING_DURATION_S = 4.0;
const NOISE_FLOOR_OFFSET_DB = 15;

// -- Peak Detection Config --
const MIN_PEAK_DISTANCE = 15;
// NEW: Configuration to ignore the edges of the spectrum.
// 5 means we will ignore the bottom 5% and top 5% of the 4096 bins.
const EDGE_IGNORE_PERCENT = 20;

// -- Audio/UI Scaling --
const MIN_DB_FOR_SCALE = 70;
const MAX_DB_FOR_SCALE = 200;
const PEAK_HOLD_DURATION_MS = 5000;


const ad9361_settings = {
    sampling_frequency: 61440000,
    rx_rf_bandwidth: 5000000,
    tx_rf_bandwidth: 18000000,
    rx_lo_frequency: 381500000,
    tx_lo_frequency: 2450000000,
    rx_gain: 30,
    rx_gain_mode: "Manual",
    tx_gain: -10
}

/* const ddc_settings = {
    enabled: true,
    frequency: 0,
    decimation: 20,
    input_sampling_frequency: 61440000,
    output_sampling_frequency: 3072000,
    max_input_sampling_frequency: 69444444.44444445
} */

const spectrometer_settings = {
    input: "DDC",
    input_sampling_frequency: 3072000,
    output_sampling_frequency: 19.736842105263158,
    number_integrations: 38,
    fft_size: 4096,
    mode: "PeakDetect"
}

const reduceRx_gainAfterNoiseScan = 5;

// --- Helper Functions ---

/**
 * A simple JS implementation to find peaks in an array.
 * @param {Float32Array} data - The spectrum data in dB.
 * @param {object} options - Options like height, distance, and edgeIgnorePercent.
 * @returns {Array} - An array of objects, each with an 'index' and 'strength'.
 */
function findPeaksJS(data, options) {
    const { height, distance, edgeIgnorePercent } = options;
    const peaks = [];

    // NEW: Calculate how many bins to ignore on each side.
    const binsToIgnore = Math.floor(data.length * (edgeIgnorePercent / 100));

    let lastPeakIndex = -Infinity;

    // NEW: Modify the loop to start after the ignored bins and end before them.
    for (let i = binsToIgnore; i < data.length - binsToIgnore; i++) {
        // The core peak condition is unchanged.
        const isPeak = data[i] > data[i - 1] && data[i] > data[i + 1];
        const isAboveThreshold = data[i] > height[i];
        const isDistant = i - lastPeakIndex > distance;

        if (isPeak && isAboveThreshold && isDistant) {
            peaks.push({ index: i, strength: data[i] });
            lastPeakIndex = i;
        }
    }
    return peaks;
}

// scaleValue function is unchanged
function scaleValue(value, in_min, in_max, out_min, out_max) {
    let scaled = ((value - in_min) * (out_max - out_min)) / (in_max - in_min) + out_min;
    return Math.max(out_min, Math.min(out_max, Math.round(scaled)));
}

// --- The React Component ---

function PlutoPeakDetector() {
    const [status, setStatus] = useState('Disconnected');
    const [scaledStrength, setScaledStrength] = useState(0);
    const [lastPeakDb, setLastPeakDb] = useState(-120);
    const [isBarActive, setIsBarActive] = useState(false);

    // Refs are unchanged
    const noiseProfile = useRef(null);
    const audioContext = useRef(null);
    const oscillator = useRef(null);
    const gainNode = useRef(null);
    const websocket = useRef(null);
    const peakHoldTimeout = useRef(null);

    // useEffect for WebSocket setup/teardown is unchanged
    useEffect(() => {
        // This function orchestrates the entire startup sequence
        const startSdr = async () => {
            const isConfigured = await configureSdr();

            if (isConfigured) {
                // Now that settings are sent, connect to the WebSocket
                connectWebSocket();
            } else {
                // Don't proceed if configuration failed
                setStatus('Config ailed. Check console.');
            }
        };

        startSdr();

        // Cleanup function is still important
        return () => {
            if (websocket.current) {
                websocket.current.close();
            }
            clearTimeout(peakHoldTimeout.current);
        };
        // eslint-disable-next-line
    }, []);

    function connectWebSocket() {
        setStatus('Connecting to WebSocket...');
        websocket.current = new WebSocket(WEBSOCKET_URL);
        websocket.current.binaryType = 'arraybuffer';
        websocket.current.onopen = () => {
            setStatus(`Profiling Noise for ${PROFILING_DURATION_S}s...`);
            profileNoise();
        };
        websocket.current.onclose = () => {
            setStatus('Disconnected.'); // No retry logic here, a refresh will restart the sequence
            clearTimeout(peakHoldTimeout.current);
        };
        websocket.current.onerror = (err) => {
            console.error("WebSocket Error:", err);
            setStatus("WebSocket Error. Check console.");
            websocket.current.close();
        };
    }
    async function configureGain() {
        setStatus('Configuring SDR Gain...');
        console.log("Sending configuration to PlutoSDR:");

        // A helper to make the fetch calls cleaner
        const sendSetting = async (endpoint, value) => {
            const url = `https://${PLUTO_IP}/api/${endpoint}`;
            const body = JSON.stringify({ ...value });
            const response = await fetch(url, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body,
            });
            if (!response.ok) {
                throw new Error(`Failed to set ${endpoint}: ${response.statusText}`);
            }
            console.log(`Successfully set ${endpoint} to ${value}`);
        };

        try {
            await sendSetting('ad9361', { rx_gain: ad9361_settings.rx_gain - reduceRx_gainAfterNoiseScan });

            console.log("SDR Gain configuration successful!");
            return true;
        } catch (error) {
            console.error("SDR Gain Configuration Failed:", error);
            setStatus(`Error: ${error.message}`);
            return false;
        }
    }

    // NEW: Function to configure the SDR via REST API
    async function configureSdr() {
        setStatus('Configuring SDR...');
        console.log("Sending configuration to PlutoSDR:");

        // A helper to make the fetch calls cleaner
        const sendSetting = async (endpoint, value) => {
            const url = `https://${PLUTO_IP}/api/${endpoint}`;
            const body = JSON.stringify({ ...value });
            const response = await fetch(url, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body,
            });
            if (!response.ok) {
                throw new Error(`Failed to set ${endpoint}: ${response.statusText}`);
            }
            console.log(`Successfully set ${endpoint} to ${value}`);
        };

        try {
            await sendSetting('ad9361', ad9361_settings);
            await sendSetting('spectrometer', spectrometer_settings);

            console.log("SDR configuration successful!");
            return true;
        } catch (error) {
            console.error("SDR Configuration Failed:", error);
            setStatus(`Error: ${error.message}`);
            return false;
        }
    }

    // profileNoise function is unchanged
    function profileNoise() {
        const noiseSamples = [];
        const startTime = Date.now();
        const profilingHandler = (event) => {
            const linearData = new Float32Array(event.data);
            const spectrumDb = linearData.map(v => 20 * Math.log10(v + 1e-9));
            noiseSamples.push(spectrumDb);
            if (Date.now() - startTime > PROFILING_DURATION_S * 1000) {
                const sampleCount = noiseSamples.length;
                const tempProfile = new Float32Array(4096).fill(0);
                for (const sample of noiseSamples) {
                    for (let i = 0; i < 4096; i++) tempProfile[i] += sample[i];
                }
                noiseProfile.current = tempProfile.map(v => v / sampleCount);
                setStatus('Running');
                websocket.current.removeEventListener('message', profilingHandler);
                configureGain();
                websocket.current.addEventListener('message', detectionHandler);
            }
        };
        websocket.current.addEventListener('message', profilingHandler);
    }

    function detectionHandler(event) {
        if (!noiseProfile.current) return;

        const linearData = new Float32Array(event.data);
        const spectrumDb = linearData.map(v => 20 * Math.log10(v + 1e-9));
        const dynamicThreshold = noiseProfile.current.map(n => n + NOISE_FLOOR_OFFSET_DB);

        const peaks = findPeaksJS(spectrumDb, {
            height: dynamicThreshold,
            distance: MIN_PEAK_DISTANCE,
            edgeIgnorePercent: EDGE_IGNORE_PERCENT,
        });

        if (peaks.length > 0) {
            // New pulse detected, so clear the reset timer.
            clearTimeout(peakHoldTimeout.current);

            const strongestPeakInFrame = peaks.reduce((prev, curr) => (curr.strength > prev.strength ? curr : prev));

            const newScaledStrength = scaleValue(
                strongestPeakInFrame.strength,
                MIN_DB_FOR_SCALE,
                MAX_DB_FOR_SCALE,
                1,
                10
            );

            // --- PEAK-OF-PEAKS LOGIC ---
            // Use functional updates to prevent stale state issues and compare with the *current* max.

            setScaledStrength(prevMaxStrength => {
                // Only update if the new peak is stronger than the one we're currently holding.
                return newScaledStrength > prevMaxStrength ? newScaledStrength : prevMaxStrength;
            });

            setLastPeakDb(prevMaxDb => {
                // Same logic for the raw dB value.
                return strongestPeakInFrame.strength > prevMaxDb ? strongestPeakInFrame.strength : prevMaxDb;
            });

            // Set bar to active and play sound on any detection within the event window
            setIsBarActive(true);
            playSound(newScaledStrength);

            // Set a new timeout to clear the bar after the hold duration.
            peakHoldTimeout.current = setTimeout(() => {
                setScaledStrength(0);
                setIsBarActive(false);
                // Also reset the stored max dB value for the next event.
                setLastPeakDb(-120);
            }, PEAK_HOLD_DURATION_MS);
        }
    }

    // Audio Logic and UI JSX are unchanged
    // ... (rest of the component is identical to the previous version)
    function initializeAudio() { if (!audioContext.current) { audioContext.current = new (window.AudioContext || window.webkitAudioContext)(); oscillator.current = audioContext.current.createOscillator(); gainNode.current = audioContext.current.createGain(); oscillator.current.type = 'sine'; oscillator.current.connect(gainNode.current); gainNode.current.connect(audioContext.current.destination); oscillator.current.start(); } }
    function playSound(scaledStrength) { if (!audioContext.current) return; const baseFrequency = 200; const frequency = baseFrequency + (scaledStrength * 50); const now = audioContext.current.currentTime; gainNode.current.gain.cancelScheduledValues(now); gainNode.current.gain.setValueAtTime(0, now); gainNode.current.gain.linearRampToValueAtTime(0.5, now + 0.01); gainNode.current.gain.linearRampToValueAtTime(0, now + 0.1); oscillator.current.frequency.setValueAtTime(frequency, now); }
    return (<div style={styles.container} onClick={initializeAudio}> <p style={styles.status}>Status: {status}</p> {status !== 'Running' && <p>Click anywhere to enable audio.</p>} <div style={styles.meter}> <div style={{ ...styles.bar, width: `${scaledStrength * 10}%`, backgroundColor: isBarActive ? '#4caf50' : '#d3d3d3' }}></div> </div> <p style={styles.strengthText}> Strength: {scaledStrength} / 10 </p> <p style={styles.dbText}> (Last Peak: {lastPeakDb.toFixed(2)} dB) </p> </div>);
}
const styles = { container: { fontFamily: 'monospace', textAlign: 'center', border: '1px solid #ccc', padding: '20px', borderRadius: '8px', cursor: 'pointer' }, status: { fontSize: '1.2em', margin: '0 0 20px 0' }, meter: { width: '100%', height: '50px', border: '2px solid #333', backgroundColor: '#eee', borderRadius: '5px', overflow: 'hidden' }, bar: { height: '100%', transition: 'width 0.1s linear, background-color 0.5s ease' }, strengthText: { fontSize: '2em', fontWeight: 'bold', margin: '20px 0 5px 0' }, dbText: { color: '#666', margin: 0 }, };
export default PlutoPeakDetector;